/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { startOfMonth, subDays, subMonths, endOfMonth } from 'date-fns';

/**
 * Dashboard feature related request structures are defined here
 */
export const API = {
  POST_TOKEN_INFO: {
    path: '/auth/introspect-tokens',
    method: 'POST',
  },
  GET_EMBED_TOKEN: {
    path: '/organizations/:organizationId/visuals-embed/token',
    method: 'GET',
  },
  GET_VISUALS: {
    path: '/organizations/:organizationId/visuals?:query',
    method: 'GET',
  },
  GET_VISUAL_FILTERS: {
    path: '/organizations/:organizationId/visual-filters?:query',
    method: 'GET',
  },
  GET_ALL_HOTELS: {
    path: '/organizations/:organizationId/hotels?:query',
    method: 'GET',
  },

  GET_ALL_BREAKDOWNS: {
    path: '/organizations/:organizationId/breakdowns?:query',
    method: 'GET',
  },
  GET_FORECAST_FILTERS: {
    path: '/organizations/:organizationId/hotels/:hotelId/forecast-filters?:query',
    method: 'GET',
  },
  POST_SQL_WAREHOUSE_PING: {
    path: '/sql-warehouse-ping',
    method: 'POST',
  },
  GET_SYSTEM_INFORMATION: {
    path: '/system-information',
    method: 'GET',
  },
  GET_ALL_UTM_BREAKDOWNS: {
    path: '/organizations/:organizationId/utm-breakdowns?:query',
    method: 'GET',
  },
  POST_COMMAND_FILTER: {
    path: '/organizations/:organizationId/users/:userId/configurations/command-filter',
    method: 'POST',
  },
  GET_COMMAND_FILTER: {
    path: '/organizations/:organizationId/users/:userId/configurations/command-filter?:query',
    method: 'GET',
  },
  DELETE_COMMAND_FILTER: {
    path: '/organizations/:organizationId/users/:userId/configurations/command-filter?:query',
    method: 'DELETE',
  },
  GET_ALL_HOTEL_GROUPS: {
    path: '/organizations/:organizationId/users/:userId/configurations/hotel-group?:query',
    method: 'GET',
  },
  POST_HOTEL_GROUP: {
    path: '/organizations/:organizationId/users/:userId/configurations/hotel-group',
    method: 'POST',
  },
  DELETE_HOTEL_GROUP: {
    path: '/organizations/:organizationId/users/:userId/configurations/hotel-group?:query',
    method: 'DELETE',
  },
  GET_NOTIFICATIONS: {
    path: '/organizations/:organizationId/users/:userId/notifications?:query',
    method: 'GET',
  },
  UPDATE_NOTIFICATION: {
    path: '/organizations/:organizationId/users/:userId/notifications/:notificationId',
    method: 'PATCH',
  },
  ADD_HOTEL_TO_FAVORITE: {
    path: '/organizations/:organizationId/users/:userId/configurations/hotel-favorites/:hotelId',
    method: 'PATCH',
  },
  ADD_HOTEL_GROUP_TO_FAVORITE: {
    path: '/organizations/:organizationId/users/:userId/configurations/hotel-group',
    method: 'POST',
  },
  POST_PREDEFINED_HOTEL_GROUP: {
    path: '/organizations/:organizationId/configurations/hotel-group',
    method: 'POST',
  },
  GET_PREDEFINED_HOTEL_GROUPS: {
    path: '/organizations/:organizationId/users/:userId/configurations/assigned-hotel-groups',
    method: 'GET',
  },
  GET_JS_VISUAL_DATA: {
    path: '/organizations/:organizationId/data',
    method: 'POST',
  },
  POST_GENERATE_ADVANCED_QUERY_LLM: {
    path: '/organizations/:organizationId/users/:userId/hotels/queries/advanced',
    method: 'POST',
  },
  GET_GENERATE_ADVANCED_QUERY_LLM: {
    path: '/organizations/:organizationId/users/:userId/hotels/queries/advanced?:query',
    method: 'GET',
  },
  POST_EXPORT_VISUAL_IMAGE: {
    path: '/organizations/:organizationId/visual-export?:query',
    method: 'POST',
  },
  POST_EXPORT_VISUAL_DATA: {
    path: '/organizations/:organizationId/visual-data-export',
    method: 'POST',
  },
  POST_FEEDBACK: {
    path: '/organizations/:organizationId/users/:userId/feedback',
    method: 'POST',
  },
  GET_VI_RESPONSE_LIST: {
    path: '/organizations/:organizationId/users/:userId/vi-agent-response?:query',
    method: 'GET',
  },
  PATCH_VI_RESPONSE: {
    path: '/organizations/:organizationId/vi-agent-response/:responseId',
    method: 'PATCH',
  },
  GET_VI_RESPONSE_BY_ID: {
    path: '/organizations/:organizationId/users/:userId/vi-agent-response/:shareId',
    method: 'GET',
  },
};
/**
 * Tags for Visual Filter classification are defined here
 */
export const TAGS = {
  CHAIN_NAME: 'CHAIN_NAME',
  TARGET_DATE: 'TARGET_DATE',
  BOOKING_DATE: 'BOOKING_DATE',
  DATE_COMPARISON: 'DATE_COMPARISON',
  HOTEL_NAME: 'HOTEL_NAME',
  SEGMENT_BREAKDOWN: 'SEGMENT_BREAKDOWN',
  PMS_SYNC: 'PMS_SYNC',
  PACE_HORIZON: 'PACE_HORIZON',
  REGION: 'REGION',
  PACE_VARIABLE: 'PACE_VARIABLE',
  ROOM_FORECAST: 'ROOM_FORECAST',
  REVENUE_FORECAST: 'REVENUE_FORECAST',
  PMS_SYNC_FORECAST: 'PMS_SYNC_FORECAST',
  PACE_FORECAST: 'PACE_FORECAST',
  SUMMARY_PACE_VARIABLE: 'SUMMARY_PACE_VARIABLE',
  SYMMETRIC_COMPARISON: 'SYMMETRIC_COMPARISON',
  DETAILED: 'DETAILED',
  NORMALIZED: 'NORMALIZED',
  CUSTOM_DATE_COMPARISON: 'CUSTOM_DATE_COMPARISON',
  CUSTOM: 'CUSTOM',
  SEGMENT_GROUP_1: 'SEGMENT_GROUP_1',
  SEGMENT_GROUP_2: 'SEGMENT_GROUP_2',
  AGGREGATION: 'AGGREGATION',
  WEB_CLICK: 'WEB_CLICK',
  SCALING_FACTOR: 'SCALING_FACTOR',
  CLICK_STEPS: 'CLICK_STEPS',
  WEB_CLICK_CHANNEL: 'WEB_CLICK_CHANNEL',
  HOTEL_NAME_ORDER: 'HOTELNAME_ORDER',
  SUMMARY: 'SUMMARY',
  SUMMARY_SEGMENT_BREAKDOWN: 'SUMMARY_SEGMENT_BREAKDOWN',
  PACE_WIDGET: 'PACE_WIDGET',
  I_PREFER_SEGMENT_BREAKDOWN: 'I_PREFER_SEGMENT_BREAKDOWN',
  METRIC_SELECTION: 'METRIC_SELECTION',
  UTM_BREAKDOWN: 'UTM_BREAKDOWN',
  UTM_FOCUS_ON: 'UTM_FOCUS_ON',
  IS_FORECAST: 'IS_FORECAST',
  PERCENTAGE: 'PERCENTAGE',
  HOTEL_GROUP_1: 'HOTEL_GROUP_1',
  HOTEL_GROUP_2: 'HOTEL_GROUP_2',
  HOTEL_GROUP_ORDER: 'HOTEL_GROUP_ORDER',
  I_PREFER_TIME_FRAME: 'I_PREFER_TIME_FRAME',
  HOTEL_ID: 'HOTEL_ID',
  FAR_FIELD_PMS_SYNC: 'FAR_FIELD_PMS_SYNC',
  GRANULARITY: 'GRANULARITY',
  GRANULARITY_BD: 'GRANULARITY_BD',
  SEGMENT_WISE_BREAKDOWN: 'SEGMENT_WISE_BREAKDOWN',
  TREND_HOTEL_PMS_SYNC: 'TREND_HOTEL_PMS_SYNC',
  MOVING_AVERAGE: 'MOVING_AVERAGE',
  CURRENCY: 'CURRENCY',
  ARRIVAL_DATE: 'ARRIVAL_DATE',
  DATE_SWITCH: 'DATE_SWITCH',
  PACE: 'PACE',
  TREND: 'TREND',
  BREAKDOWN_SUMMARY: 'BREAKDOWN_SUMMARY',
  MAX_STAY_DATE: 'MAX_STAY_DATE',
  COMPARISON_STAY_DATE: 'COMPARISON_STAY_DATE',
  COMPARISON_BOOKING_DATE: 'COMPARISON_BOOKING_DATE',
};
/**
 * Operators to generate Power BI schema are defined here
 */
export const OPERATORS = {
  NOT_IN: 'NotIn',
  ALL: 'All',
  GREATER_OR_EQUAL: 'GreaterThanOrEqual',
  LESS_OR_EQUAL: 'LessThanOrEqual',
  AND: 'And',
  IN: 'In',
};
/**
 * Default booking date is defined here
 */
export const BOOKING_DATE = '2015/01/01';

/**
 * Report type of each widget is defined here
 */
export const REPORT_TYPE = {
  SUMMARY: 'Summary',
  SUMMARY_HOTEL: 'Summary-Hotel',
  TREND_TIME: 'Trend-Time',
  MUI_TREND_TIME: 'Mui-trend-Time',
  TREND_SEGMENT: 'Trend-Segment',
  TREND_TIME_TABULAR: 'Trend-Time-Tabular',
  TREND_SEGMENT_TABULAR: 'Trend-Segment-Tabular',
  WEEKLY_TREND_TIME: 'Weekly-Trend-Time',
  WEEKLY_TREND_SEGMENT: 'Weekly-Trend-Segment',
  WEEKLY_TREND_SEGMENT_TABULAR: 'Weekly-Trend-Segment-Tabular',
  WEEKLY_TREND_TIME_TABULAR: 'Weekly-Trend-Time-Tabular',
  TREND_TIME_CUMULATIVE: 'Cumulative-Trend-Time',
  TREND_SEGMENT_CUMULATIVE: 'Cumulative-Trend-Segment',
  TREND_TIME_CUMULATIVE_TABULAR: 'Cumulative-Trend-Time-Tabular',
  TREND_SEGMENT_CUMULATIVE_TABULAR: 'Cumulative-Trend-Segment-Tabular',
  REGION_MAP: 'Region',
  PACE_TIME_WIDGET: 'Pace-Time-Widget',
  PACE_SEGMENT_WIDGET: 'Pace-Segment-Widget',
  PACE_HOTEL_WIDGET: 'Pace-Hotel-Widget',
  CUMULATIVE: 'Cumulative',
  WEB_CLICK: 'Web-Click',
  WEEKLY_WEB_CLICK: 'web-click-weekly',
  CUMULATIVE_WEB_CLICK: 'Web-Click-Cumulative',
  PACE_TIME_WIDGET_LAG_ALIGNED: 'Pace-Time-Widget-Lag-Aligned',
  PACE_SEGMENT_WIDGET_LAG_ALIGNED: 'Pace-Segment-Widget-Lag-Aligned',
  PACE_HOTEL_WIDGET_LAG_ALIGNED: 'Pace-Hotel-Widget-Lag-Aligned',
  PACE_TIME_WIDGET_ABSOLUTE_CUMULATIVE: 'Pace-Time-Widget-Absolute-Cumulative',
  PACE_SEGMENT_WIDGET_ABSOLUTE_CUMULATIVE: 'Pace-Segment-Widget-Absolute-Cumulative',
  PACE_HOTEL_WIDGET_ABSOLUTE_CUMULATIVE: 'Pace-Hotel-Widget-Absolute-Cumulative',
  PACE_TIME_WIDGET_LAG_ALIGNED_CUMULATIVE: 'Pace-Time-Widget-Lag-Aligned-Cumulative',
  PACE_SEGMENT_WIDGET_LAG_ALIGNED_CUMULATIVE: 'Pace-Segment-Widget-Lag-Aligned-Cumulative',
  PACE_HOTEL_WIDGET_LAG_ALIGNED_CUMULATIVE: 'Pace-Hotel-Widget-Lag-Aligned-Cumulative',
  I_PREFER: 'iPrefer',
  WORKBOOK_REPORT: 'Workbook-Report',
  WORKBOOK_REPORT_HOTEL: 'Workbook-Report-Hotel',
  WORKBOOK_REPORT_RATE_PLAN: 'Workbook-Report-Rate-Plan',
  WORKBOOK_REPORT_IATA_ON: 'Workbook-Report-IATA-Details-On',
  WORKBOOK_REPORT_IATA_OFF: 'Workbook-Report-IATA-Details-Off',
  PACE: 'Pace',
  PACE_TEST: 'Pace-Test',
  TREND_TIME_PERCENTAGE: 'Trend-Time-Percentage',
  TREND_TIME_PERCENTAGE_TABULAR: 'Trend-Time-Percentage-Tabular',
  TREND_TIME_PERCENTAGE_TABULAR_COMBINED: 'Trend-Time-Percentage-Tabular-Combined',
  TREND_SEGMENT_PERCENTAGE: 'Trend-Segment-Percentage',
  TREND_SEGMENT_PERCENTAGE_TABULAR: 'Trend-Segment-Percentage-Tabular',
  TREND_SEGMENT_PERCENTAGE_TABULAR_COMBINED: 'Trend-Segment-Percentage-Tabular-Combined',

  // Trend Time tags - combined tabular
  TREND_TIME_TABULAR_DAILY: 'Trend-Time-Tabular-Daily',
  TREND_TIME_TABULAR_WEEKLY_COMBINED: 'Trend-Time-Tabular-Weekly-Combined',
  TREND_TIME_TABULAR_CUMULATIVE_COMBINED: 'Trend-Time-Tabular-Cumulative-Combined',
  // Trend Segment tags - combined tabular
  TREND_SEGMENT_TABULAR_DAILY_COMBINED: 'Trend-Segment-Tabular-Combined',
  TREND_SEGMENT_TABULAR_WEEKLY_COMBINED: 'Trend-Segment-Tabular-Weekly-Combined',
  TREND_SEGMENT_TABULAR_CUMULATIVE_COMBINED: 'Trend-Segment-Tabular-Cumulative-Combined',
  //
  TREND_TIME_SEGMENT: 'Trend-Time-Segment',
  TREND_TIME_SEGMENT_TABULAR: 'Trend-Time-Segment-Tabular',
  WEEKLY_TREND_TIME_SEGMENT: 'Weekly-Trend-Time-Segment',
  WEEKLY_TREND_TIME_SEGMENT_TABULAR: 'Weekly-Trend-Time-Segment-Tabular',
  TREND_TIME_SEGMENT_CUMULATIVE: 'Cumulative-Trend-Time-Segment',
  TREND_TIME_SEGMENT_CUMULATIVE_TABULAR: 'Cumulative-Trend-Time-Segment-Tabular',
  // trend time segment combined tabular
  TREND_TIME_SEGMENT_TABULAR_DAILY_COMBINED: 'Trend-Time-Segment-Tabular-Combined',
  TREND_TIME_SEGMENT_TABULAR_WEEKLY_COMBINED: 'Trend-Time-Segment-Tabular-Weekly-Combined',
  TREND_TIME_SEGMENT_TABULAR_CUMULATIVE_COMBINED: 'Trend-Time-Segment-Tabular-Cumulative-Combined',
  // Trend Hotel tags - forecast on
  TREND_HOTEL: 'Trend-Hotel',
  TREND_HOTEL_TABULAR: 'Trend-Hotel-Tabular',
  WEEKLY_TREND_HOTEL: 'Weekly-Trend-Hotel',
  WEEKLY_TREND_HOTEL_TABULAR: 'Weekly-Trend-Hotel-Tabular',
  TREND_HOTEL_CUMULATIVE: 'Cumulative-Trend-Hotel',
  TREND_HOTEL_CUMULATIVE_TABULAR: 'Cumulative-Trend-Hotel-Tabular',
  TREND_HOTEL_PERCENTAGE: 'Trend-Hotel-Percentage',
  TREND_HOTEL_PERCENTAGE_TABULAR: 'Trend-Hotel-Percentage-Tabular',
  TREND_HOTEL_PERCENTAGE_TABULAR_COMBINED: 'Trend-Hotel-Percentage-Tabular-Combined',
  // Trend Hotel tags - combined tabular
  TREND_HOTEL_TABULAR_COMBINED: 'Trend-Hotel-Tabular-Combined',
  TREND_HOTEL_TABULAR_WEEKLY_COMBINED: 'Trend-Hotel-Tabular-Weekly-Combined',
  TREND_HOTEL_TABULAR_CUMULATIVE_COMBINED: 'Trend-Hotel-Tabular-Cumulative-Combined',
  // Trend Hotel tags - forecast off
  TREND_HOTEL_FORECAST_OFF: 'Trend-Hotel-Forecast-Off',
  TREND_HOTEL_TABULAR_FORECAST_OFF: 'Trend-Hotel-Tabular-Forecast-Off',
  WEEKLY_TREND_HOTEL_FORECAST_OFF: 'Weekly-Trend-Hotel-Forecast-Off',
  WEEKLY_TREND_HOTEL_TABULAR_FORECAST_OFF: 'Weekly-Trend-Hotel-Tabular-Forecast-Off',
  TREND_HOTEL_CUMULATIVE_FORECAST_OFF: 'Cumulative-Trend-Hotel-Forecast-Off',
  TREND_HOTEL_CUMULATIVE_TABULAR_FORECAST_OFF: 'Cumulative-Trend-Hotel-Tabular-Forecast-Off',
  TREND_HOTEL_PERCENTAGE_FORECAST_OFF: 'Trend-Hotel-Percentage-Forecast-Off',
  TREND_HOTEL_PERCENTAGE_TABULAR_FORECAST_OFF: 'Trend-Hotel-Percentage-Tabular-Forecast-Off',
  // pace time tabular combined tags
  PACE_TIME_TABULAR_DAILY_COMBINED: 'Pace-Time-Tabular-Combined',
  PACE_TIME_TABULAR_LAG_ALIGNED_COMBINED: 'Pace-Time-Tabular-Lag-Aligned-Combined',
  PACE_TIME_TABULAR_CUMULATIVE_COMBINED: 'Pace-Time-Tabular-Cumulative-Combined',
  PACE_TIME_TABULAR_CUMULATIVE_LAG_ALIGNED_COMBINED:
    'Pace-Time-Tabular-Cumulative-Lag-Aligned-Combined',
  // pace hotel tabular combined tags
  PACE_HOTEL_TABULAR_DAILY_COMBINED: 'Pace-Hotel-Tabular-Combined',
  PACE_HOTEL_TABULAR_LAG_ALIGNED_COMBINED: 'Pace-Hotel-Tabular-Lag-Aligned-Combined',
  PACE_HOTEL_TABULAR_CUMULATIVE_COMBINED: 'Pace-Hotel-Tabular-Cumulative-Combined',
  PACE_HOTEL_TABULAR_CUMULATIVE_LAG_ALIGNED_COMBINED:
    'Pace-Hotel-Tabular-Cumulative-Lag-Aligned-Combined',
  // pace segment tabular combined tags
  PACE_SEGMENT_TABULAR_DAILY_COMBINED: 'Pace-Segment-Tabular-Combined',
  PACE_SEGMENT_TABULAR_LAG_ALIGNED_COMBINED: 'Pace-Segment-Tabular-Lag-Aligned-Combined',
  PACE_SEGMENT_TABULAR_CUMULATIVE_COMBINED: 'Pace-Segment-Tabular-Cumulative-Combined',
  PACE_SEGMENT_TABULAR_CUMULATIVE_LAG_ALIGNED_COMBINED:
    'Pace-Segment-Tabular-Cumulative-Lag-Aligned-Combined',
  // trend moving average tags
  TREND_TIME_MOVING_AVERAGE: 'Trend-Time-Moving-Average',
  TREND_SEGMENT_MOVING_AVERAGE: 'Trend-Segment-Moving-Average',
  TREND_HOTEL_MOVING_AVERAGE: 'Trend-Hotel-Moving-Average',
  TREND_TIME_MOVING_AVERAGE_TABULAR_DAILY: 'Trend-Time-Moving-Average-Tabular-Daily',
  TREND_HOTEL_MOVING_AVERAGE_TABULAR_DAILY: 'Trend-Hotel-Moving-Average-Tabular-Daily',
  TREND_SEGMENT_MOVING_AVERAGE_TABULAR_DAILY: 'Trend-Segment-Moving-Average-Tabular-Daily',
  //
  TREND_HOTEL_SEGMENT: 'Trend-Hotel-Segment',
  TREND_HOTEL_SEGMENT_TABULAR: 'Trend-Hotel-Segment-Tabular',
  WEEKLY_TREND_HOTEL_SEGMENT: 'Weekly-Trend-Hotel-Segment',
  WEEKLY_TREND_HOTEL_SEGMENT_TABULAR: 'Weekly-Trend-Hotel-Segment-Tabular',
  TREND_HOTEL_SEGMENT_CUMULATIVE: 'Cumulative-Trend-Hotel-Segment',
  TREND_HOTEL_SEGMENT_CUMULATIVE_TABULAR: 'Cumulative-Trend-Hotel-Segment-Tabular',
  // trend hotel segment combined tabular
  TREND_HOTEL_SEGMENT_TABULAR_DAILY_COMBINED: 'Trend-Hotel-Segment-Tabular-Combined',
  TREND_HOTEL_SEGMENT_TABULAR_WEEKLY_COMBINED: 'Trend-Hotel-Segment-Tabular-Weekly-Combined',
  TREND_HOTEL_SEGMENT_TABULAR_CUMULATIVE_COMBINED:
    'Trend-Hotel-Segment-Tabular-Cumulative-Combined',
  // pace time segment
  PACE_TIME_SEGMENT_WIDGET: 'Pace-Time-Segment-Widget',
  PACE_TIME_SEGMENT_WIDGET_LAG_ALIGNED: 'Pace-Time-Segment-Widget-Lag-Aligned',
  PACE_TIME_SEGMENT_WIDGET_ABSOLUTE_CUMULATIVE: 'Pace-Time-Segment-Widget-Absolute-Cumulative',
  PACE_TIME_SEGMENT_WIDGET_LAG_ALIGNED_CUMULATIVE:
    'Pace-Time-Segment-Widget-Lag-Aligned-Cumulative',
  PACE_TIME_SEGMENT_TABULAR_DAILY_COMBINED: 'Pace-Time-Segment-Tabular-Daily-Combined',
  PACE_TIME_SEGMENT_TABULAR_LAG_ALIGNED_COMBINED: 'Pace-Time-Segment-Tabular-Lag-Aligned-Combined',
  PACE_TIME_SEGMENT_TABULAR_CUMULATIVE_COMBINED: 'Pace-Time-Segment-Tabular-Cumulative-Combined',
  PACE_TIME_SEGMENT_TABULAR_CUMULATIVE_LAG_ALIGNED_COMBINED:
    'Pace-Time-Segment-Tabular-Cumulative-Lag-Aligned-Combined',
  // pace hotel segment
  PACE_TIME_HOTEL_WIDGET: 'Pace-Time-Hotel-Widget',
  PACE_TIME_HOTEL_WIDGET_LAG_ALIGNED: 'Pace-Time-Hotel-Widget-Lag-Aligned',
  PACE_TIME_HOTEL_WIDGET_ABSOLUTE_CUMULATIVE: 'Pace-Time-Hotel-Widget-Absolute-Cumulative',
  PACE_TIME_HOTEL_WIDGET_LAG_ALIGNED_CUMULATIVE: 'Pace-Time-Hotel-Widget-Lag-Aligned-Cumulative',
  PACE_TIME_HOTEL_TABULAR_DAILY_COMBINED: 'Pace-Hotel-Segment-Tabular-Daily-Combined',
  PACE_TIME_HOTEL_TABULAR_LAG_ALIGNED_COMBINED: 'Pace-Hotel-Segment-Tabular-Lag-Aligned-Combined',
  PACE_TIME_HOTEL_TABULAR_CUMULATIVE_COMBINED: 'Pace-Hotel-Segment-Tabular-Cumulative-Combined',
  PACE_TIME_HOTEL_TABULAR_CUMULATIVE_LAG_ALIGNED_COMBINED:
    'Pace-Hotel-Segment-Tabular-Cumulative-Lag-Aligned-Combined',
  //
  TREND_SEGMENT_RIBBON: 'Trend-Segment-Ribbon',
  WEEKLY_TREND_SEGMENT_RIBBON: 'Weekly-Trend-Segment-Ribbon',
  SEARCH: 'Search',
};
/**
 * Short cut keys for the functionalities are defined here
 */
export const SHORTCUT_KEY = {
  PMS_TOGGLE: ['Alt', 'p'],
  WEEKLY_TOGGLE: ['Alt', 'w'],
  BACK_BY_MONTH: ['Control', 'ArrowLeft'],
  BACK_BY_MONTH_MAC: ['Meta', 'ArrowLeft'],
  BACK_BY_PERIOD: ['ArrowLeft'],
  FORWARD_BY_MONTH: ['Control', 'ArrowRight'],
  FORWARD_BY_MONTH_MAC: ['Meta', 'ArrowRight'],
  FORWARD_BY_PERIOD: ['ArrowRight'],
};
/**
 * Value of OTB date comparison defined here
 */
export const OTB_VALUE = 'vs OTB';
/**
 * Value of Custom date comparison define here
 */
export const CUSTOM_VALUE = 'Custom';
/**
 * Not applicable value for date comparison & alignment filter is defined here
 */
export const NA = 'NA';
/**
 * Filter values are defined here
 */
export const FILTER_VALUES = {
  RATE_CATEGORY: 'Rate Category',
  RATE_CATEGORY_RAW: 'Rate Category (raw)',
};
/**
 * Custom Keys are defined here
 */
export const KEYS = {
  LAST_UPDATED_DATE: 'syntheticPipelineTime',
  TIMEZONE: 'UTC',
  START_DATE: 'start',
  END_DATE: 'end',
  CUSTOM: 'Custom',
  PACE_FORECAST_LIMIT: 30,
  SET_A: 'Group A',
  SET_B: 'Group B',
  MAX_LOADED_TIME: 'maxLoadedTime',
  LAST_BOOKING_DATE_PARTIAL: 'lastBookingDatePartial',
  PARTIAL_HOURS: 'partialHours',
  LAST_BOOKING_DATE_24HRS: 'lastBookingDate24hrs',
  MAX_STAY_DATE: 'maxStayDate',
  MAX_FORECAST_DATE_ROOMS_PMS_ON: 'maxForecastDateRoomsPmsOn',
  MAX_FORECAST_DATE_ROOMS_PMS_OFF: 'maxForecastDateRoomsPmsOff',
  MAX_FORECAST_DATE_REV_PMS_ON: 'maxForecastDateRevPmsOn',
  MAX_FORECAST_DATE_REV_PMS_OFF: 'maxForecastDateRevPmsOff',
};
/**
 * Summary stay date object is defined here
 */
export const STAY_DATE = {
  startDate: new Date(subDays(new Date(), 1)).toISOString(),
  endDate: new Date(subDays(new Date(), 1)).toISOString(),
  key: 'selection',
};
/**
 * Trend tab stay date object is defined here
 */
export const TREND_STAY_DATE = {
  startDate: new Date(startOfMonth(subMonths(new Date(), 1))).toISOString(),
  endDate: new Date(endOfMonth(subMonths(new Date(), 1))).toISOString(),
  key: 'selection',
};

// Time unit constants
export const TIME_UNIT = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

// Constants for offsets
export const OFFSET = {
  YEAR: -1,
  MONTH: -1,
  WEEK: -1,
  ALIGN_DAY: -364,
  ALIGN_MONTH_DAY: -28,
  BOOKING_OTB: -1,
};

// Threshold for applying partial hours logic in booking OTB date comparison
export const BOOKING_OTB_PARTIAL_HOURS_THRESHOLD = 12;
/**
 * Per page default visual count
 */
export const DEFAULT_VISUALS_PER_PAGE = 6;
/**
 * Last Cumulative visual order in trend tabs
 */
export const DEFAULT_CUMULATIVE_VISUAL_ORDER = 5;

/**
 * Power BI report visual names
 */
export const VISUAL_PAGE_NAMES = {
  TREND_SEGMENT_TOTAL_REVENUE_C: 'ReportSectiona8da0be1a25f327cc2a1',
  TREND_SEGMENT_TOTAL_REVENUE_W: 'ReportSection3b74f8ac460241af62c3',
  TREND_SEGMENT_NO_OF_CANCELLATION_W: 'ReportSectiona231c0275716ab579ce3',
  TREND_SEGMENT_TOTAL_OCCUPIED_ROOMS_C: 'ReportSection1ffff9b588fc02fdbba1',
  TREND_SEGMENT_TOTAL_OCCUPIED_ROOMS_W: 'ReportSectionbaf46554acb641480818',
  TREND_HOTEL_TOTAL_REVENUE_C: 'ReportSectiond6705a204f72f18c9206',
  TREND_HOTEL_TOTAL_OCCUPIED_ROOMS_C: 'ReportSection87b8fd9d7cbcab7c48f3',
  TREND_HOTEL_NO_OF_CANCELLATION_W: 'ReportSectiona7025961dc3d386a06a3',
};
/**
 * Actual value for pace visual forecast is defined here
 */
export const ACTUAL_VALUE = 'Actual';

/**
 * Dashboard component slices are defined here
 */
export const SLICE = {
  BASE_AUTH: 'feature/base-auth',
  SIGN_IN: 'feature/sign-in',
  FORGOT_PASSWORD: 'feature/forgot-password',
  RESET_PASSWORD: 'feature/reset-password',
  NOTIFICATION: 'feature/notification',
  VERIFY_CALLBACK: 'feature/verify-callback',
  DASHBOARD: 'feature/dashboard',
  RATE_CALENDAR_UPLOAD: 'feature/rate-calendar-upload',
  USERS: 'feature/users',
  RATE_CALENDAR: 'feature/rate-calendar',
  CONFIGURE_EVENT_CATEGORIES: 'feature/configure-event-categories',
  SUMMARY: 'feature/summary',
  PACE_TIME: 'feature/pace-time',
  PACE_SEGMENT: 'feature/pace-segment',
  PACE: 'feature/pace',
  TREND: 'feature/trend',
  TREND_TIME: 'feature/trend-time',
  TREND_SEGMENT: 'feature/trend-segment',
  TREND_HOTEL: 'feature/trend-hotel',
  REGIONAL_MAP: 'feature/regional-map',
  WEB_CLICK: 'feature/web-click',
  I_PREFER: 'feature/i-prefer',
  WORKBOOK_REPORT: 'feature/workbook',
  TREND_TABULAR: 'feature/trend-time',
  ALERT: 'feature/alert',
  SEARCH: 'feature/search',
  CUSTOM_CHAT: 'feature/custom-chat',
};

/**
 * Alignment toggle value
 */
export const ALIGNED = 'ALIGNED';
//
export const PRIMARY = 'Breakdown';
//
export const PACE_HORIZON_SELECTION = {
  BOOKING_PACE_WINDOW: 'BPW',
  BOOKING_PACE_HORIZON: 'BPH',
};
//
export const CURRENCY_TYPES = {
  USD: 'USD',
  RC: 'RC',
};
//
export const VISUAL_HEADER_TYPES = {
  // visuals with a single header row
  SINGLE: 'SINGLE',
  SINGLE_2: 'SINGLE_2',
  // visuals with 2 header rows
  DOUBLE: 'DOUBLE',
  // visuals with 2 header rows but the dataset structure is different
  DOUBLE_2: 'DOUBLE_2',
  // visual with 2 header rows and the data is grouped by date or lag
  GROUPED_DOUBLE: 'GROUPED_DOUBLE',
};
// date types for workbook report
export const VISUAL_DATE_TYPES = {
  STAY: 'STAY',
  ARRIVAL: 'ARRIVAL',
};
// date type dropdown values
export const WORKBOOK_DATE_SELECTION = [
  { label: 'Arrival Dates', id: VISUAL_DATE_TYPES.ARRIVAL },
  { label: 'Stay Dates', id: VISUAL_DATE_TYPES.STAY },
];
// Symmetric comparison values
export const SYMMETRIC_COMPARISON_VALUES = {
  OTB: 'OTB',
  SYMMETRIC: 'Symmetric',
};
// Label order for summary breakdown options
export const labelOrder = [
  'Channel / Sub Channel',
  'Market Segment',
  'Rate Category / Rate Plan',
  'Consortia / IATA',
  'Region / Country',
];
// Visual event options
export const VISUAL_EVENTS = {
  RENDERED: 'rendered',
  LOADED: 'loaded',
  ERROR: 'error',
  VISUAL_RENDERED: 'visualRendered',
};

// Interpreter options
export const MODEL_OPTIONS = [
  { label: 'Gemini', value: 'gemini-3.1-pro-preview', provider: 'gemini' },
  { label: 'GPT', value: 'gpt-5.1', provider: 'openai' },
  { label: 'Claude', value: 'claude-sonnet-5', provider: 'vertex-anthropic' },
];

export const EXPORT_OPTIONS = {
  VISUAL: 'Visual',
  DATA_EXPORT: 'Data Export',
  VISUAL_DATA: 'Visual + Data',
  EXCEL_EXPORT: 'Excel Export',
};
// Interpreter Export types
export const OUTPUT_OPTIONS = {
  CERULEAN: [EXPORT_OPTIONS.DATA_EXPORT, EXPORT_OPTIONS.EXCEL_EXPORT],
  PBI: [
    EXPORT_OPTIONS.VISUAL,
    EXPORT_OPTIONS.DATA_EXPORT,
    EXPORT_OPTIONS.VISUAL_DATA,
    EXPORT_OPTIONS.EXCEL_EXPORT,
  ],
  RIBBON_VIEW: [EXPORT_OPTIONS.VISUAL, EXPORT_OPTIONS.EXCEL_EXPORT],
};

export const TRUE = 'true';
